import {
  type MessageHandler,
  useNativeMessageListener as useNativeMessagePackageListener
} from '@carvana/merch-native';
import { useContext, useMemo } from 'react';
import { isNonProd } from '~/constants/environment';
import ErrorContext from '~/context/common/ErrorContext';
import { generateErrorLogger } from '~/utilities/loggers';
import {
  useAddFavoriteHandler,
  useFetchFavoritesHandler,
  useRemoveFavoriteHandler,
  useVdpViewedHandler
} from './handlers';
import { ListenerEvent, type ListenerEventType } from './handlers/constants';
import useCloseModalHandler from './handlers/useCloseModalHandler';
import useIsExploreInViewHandler from './handlers/useIsExploreInViewHandler';
import useRefreshFinanceHandler from './handlers/useRefreshFinanceHandler';
import useSaveSearchHandler from './handlers/useSavedSearchHandler';
import { useErrorHandler } from '../common/handlers';

export default function useNativeMessageListener() {
  const { setNativeError } = useContext(ErrorContext);
  const errorHandler = useErrorHandler<ListenerEventType>();
  const closeModalHandler = useCloseModalHandler();
  const exploreInViewHandler = useIsExploreInViewHandler();
  const addFavoriteHandler = useAddFavoriteHandler();
  const removeFavoriteHandler = useRemoveFavoriteHandler();
  const fetchFavoritesHandler = useFetchFavoritesHandler();
  const saveSearchHandler = useSaveSearchHandler();
  const vdpViewedHandler = useVdpViewedHandler();
  const refreshFinanceHandler = useRefreshFinanceHandler();

  const handlers = useMemo<Array<MessageHandler<ListenerEventType, any>>>(
    () => [
      addFavoriteHandler,
      closeModalHandler,
      errorHandler,
      exploreInViewHandler,
      fetchFavoritesHandler,
      refreshFinanceHandler,
      removeFavoriteHandler,
      saveSearchHandler,
      vdpViewedHandler
    ],
    [
      addFavoriteHandler,
      closeModalHandler,
      errorHandler,
      exploreInViewHandler,
      fetchFavoritesHandler,
      refreshFinanceHandler,
      removeFavoriteHandler,
      saveSearchHandler,
      vdpViewedHandler
    ]
  );

  useNativeMessagePackageListener<ListenerEventType>({
    handlers,
    knownEvents: ListenerEvent,
    logErrorToTelemetry: generateErrorLogger('Home native message listener'),
    logErrorToUser(_err) {
      setNativeError(new Error('Something went wrong. Please try again.'));
    },
    verboseLogs: isNonProd
  });
}
